import { orderActivity, weeklyReviewDue } from "@szyos/domain";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { useId, useState } from "react";
import { api, errorMessage, type AuthStatus, type WorkOrderSummary } from "../api";
import { DecisionCard } from "../components/DecisionCard";
import { EmptyState, useToast } from "../components/feedback";
import { Activity } from "../components/widgets";
import { EfficiencyPanel, TrialPanel } from "../components/Efficiency";
import { createDemoWorkOrder } from "../demo";
import { useSimulation } from "../simulation";
import { OwnerTasksPanel } from "../components/OwnerTasks";
import { RolloutPanel } from "../components/Rollout";
import { orderStatus, readableReason, untilText } from "../format";
import { Icon } from "../icons";

const ACTIVE = new Set([
  "RUNNING",
  "VERIFYING",
  "REVIEW_READY",
  "ACCEPTED",
  "STAGING",
  "RELEASE_APPROVAL",
  "WAITING_HUMAN",
  "BLOCKED",
  "PAUSED_PROVIDER",
]);

const LIMIT_HINT_KEY = "szyos.limitHintSeen";

function readStored(key: string): string {
  try {
    return localStorage.getItem(key) ?? "";
  } catch {
    return "";
  }
}

/** Zustände, in denen du handeln musst. */
const NEEDS_YOU = new Set([
  "WAITING_HUMAN",
  "REVIEW_READY",
  "RELEASE_APPROVAL",
  "SPEC_REVIEW",
  "PLAN_REVIEW",
]);

/** Reihenfolge: was gerade arbeitet, was dich braucht, was wartet, zuletzt Angehaltenes. */
function orderRank(w: WorkOrderSummary): number {
  const activity = orderActivity(w);
  if (activity === "working") return 0;
  if (w.openDecisions > 0 || NEEDS_YOU.has(w.state) || w.openOwnerSteps > 0) return 1;
  if (activity === "held") return 3;
  return 2;
}

/** Kopfzeile: zählt nur belegte Arbeit als laufend. */
function activityText(list: readonly WorkOrderSummary[]): string {
  const open = list.filter((w) => ACTIVE.has(w.state));
  const working = open.filter((w) => orderActivity(w) === "working").length;
  const queued = open.filter((w) => orderActivity(w) === "queued").length;
  const held = open.filter((w) => orderActivity(w) === "held").length;
  const parts = [
    working === 0
      ? "Gerade arbeitet kein Auftrag"
      : `${working} ${working === 1 ? "Auftrag arbeitet" : "Aufträge arbeiten"} gerade`,
  ];
  if (queued > 0) parts.push(`${queued} ${queued === 1 ? "wartet" : "warten"} auf einen freien Platz`);
  if (held > 0) parts.push(`${held} angehalten`);
  return `${parts.join(", ")}.`;
}

export function Cockpit({ auth }: { auth: AuthStatus }) {
  const navigate = useNavigate();
  const toast = useToast();
  const workOrders = useQuery({ queryKey: ["work-orders"], queryFn: api.workOrders });
  const decisions = useQuery({ queryKey: ["decisions"], queryFn: () => api.decisions("PENDING") });
  const events = useQuery({ queryKey: ["recent-events"], queryFn: api.recentEvents });
  const usage = useQuery({ queryKey: ["usage"], queryFn: api.usage });
  const review = useQuery({ queryKey: ["weekly-review"], queryFn: api.weeklyReview });
  const approvals = useQuery({ queryKey: ["approvals"], queryFn: api.approvals });
  const ownerTasks = useQuery({ queryKey: ["owner-tasks"], queryFn: api.ownerTasks });
  const waiting = approvals.data?.length ?? 0;
  const simulation = useSimulation();
  const demo = useMutation({
    mutationFn: createDemoWorkOrder,
    onSuccess: (id) => {
      toast("Demo-Auftrag gestartet. Der lokale Node übernimmt die Läufe.");
      void navigate({ to: "/work-orders/$orderId", params: { orderId: id } });
    },
    onError: (err) => toast(errorMessage(err), "error"),
  });

  const list = workOrders.data ?? [];
  // Ausgelieferte Aufträge mit offenem Schritt bei dir gehören nach oben, nicht zu den erledigten.
  const isActive = (w: WorkOrderSummary) => ACTIVE.has(w.state) || w.openOwnerSteps > 0;
  // Filter nach Projekt und Archiv für Abgeschlossenes, damit die Liste nicht wächst (Wunsch des Owners 24.10.).
  const filterId = useId();
  const [projectFilter, setProjectFilter] = useState("");
  const orderProjects = [...new Map(list.map((w) => [w.projectId, w.projectTitle])).entries()].sort((a, b) =>
    a[1].localeCompare(b[1], "de"),
  );
  const shown = list.filter((w) => !projectFilter || w.projectId === projectFilter);
  const isArchived = (w: WorkOrderSummary) =>
    !isActive(w) && (w.state === "RELEASED" || w.state === "CANCELLED");
  const active = shown.filter(isActive).sort((a, b) => orderRank(a) - orderRank(b));
  const other = shown.filter((w) => !isActive(w) && !isArchived(w));
  const archived = shown.filter(isArchived);
  const open = decisions.data ?? [];
  const reviewDue = Boolean(review.data && weeklyReviewDue(review.data.lastReviewAt, new Date()));
  // Den Limit-Hinweis kannst du abhaken; er kommt erst wieder, wenn sich Stand oder Rücksetzzeit ändern.
  const nearLimits = (usage.data?.limits ?? []).filter(
    (l) => l.state === "warning" || l.state === "exhausted",
  );
  const limitKey = nearLimits.map((l) => `${l.limit}:${l.state}:${l.resetsAt ?? ""}`).join("|");
  const [dismissedLimit, setDismissedLimit] = useState(() => readStored(LIMIT_HINT_KEY));
  const limitNear = limitKey !== "" && limitKey !== dismissedLimit;
  const dismissLimit = () => {
    setDismissedLimit(limitKey);
    try {
      localStorage.setItem(LIMIT_HINT_KEY, limitKey);
    } catch {
      // Ohne Speicher gilt der Haken nur bis zum Neuladen.
    }
  };
  // Dringende Schritte bei dir gehören in „Jetzt dran“, alles andere bleibt unter „Für dich zu tun“.
  const urgentTasks = (ownerTasks.data ?? []).filter((t) => t.urgent && !t.doneAt).length;
  const nowCount =
    (open.length > 0 ? 1 : 0) + (waiting > 0 ? 1 : 0) + (reviewDue ? 1 : 0) + (limitNear ? 1 : 0);

  const orderCard = (w: WorkOrderSummary) => {
    const status = orderStatus(w);
    return (
      <li key={w.id}>
        <Link
          to="/work-orders/$orderId"
          params={{ orderId: w.id }}
          className={`order-card state-${status.cls}`}
        >
          <span className="order-main">
            <strong>{w.title}</strong>
            <span className="muted">{w.projectTitle}</span>
            {w.state === "PAUSED_PROVIDER" && (
              <span className="order-note" title={w.stateReason ? readableReason(w.stateReason) : undefined}>
                <Icon name="clock" />
                {w.pausedUntil ? `Weiter ab ${untilText(w.pausedUntil)}` : "Pausiert bis zur Rücksetzzeit"}
                {w.stateReason ? ` · ${readableReason(w.stateReason).split(",")[0]}` : ""}
              </span>
            )}
          </span>
          <span className={`chip state-chip state-${status.cls}`}>{status.label}</span>
          <span
            className="order-quota"
            aria-label={`${w.itemsDone} von ${w.itemsTotal} Aufgaben verifiziert`}
          >
            <span className="bar">
              <i
                style={{
                  ["--w" as string]: `${w.itemsTotal ? (w.itemsDone / w.itemsTotal) * 100 : 0}%`,
                  ["--accent" as string]: "var(--violet)",
                }}
              />
            </span>
            <span className="mono">
              {w.itemsDone}/{w.itemsTotal}
            </span>
          </span>
          {w.openDecisions > 0 && (
            <span className="badge-block">
              <Icon name="inbox" /> {w.openDecisions}
            </span>
          )}
        </Link>
      </li>
    );
  };

  return (
    <>
      <section className="hero" aria-labelledby="heroTitle">
        <div className="hero-text">
          <h1 id="heroTitle">
            Was braucht <span className="glow-word">dich?</span>
          </h1>
          <p className="hero-sub">
            {open.length > 0
              ? `${open.length} ${open.length === 1 ? "Entscheidung wartet" : "Entscheidungen warten"} auf dich. ${activityText(list)}`
              : active.length > 0
                ? `Keine offenen Entscheidungen. ${activityText(list)}`
                : "Alles ruhig. Halte eine Idee fest oder starte einen Auftrag."}
          </p>
        </div>
        <div className="hero-actions">
          <Link to="/ideas" search={{ neu: "1" }} className="btn btn--primary">
            <span className="btn-label">
              <Icon name="idea" /> Idee festhalten
            </span>
          </Link>
          {auth.user?.role === "owner" && simulation && (
            // Probelauf ohne KI, nur in Tests und lokaler Entwicklung; im Büro gibt es keine Simulation.
            <button
              type="button"
              className="chip-btn demo-link"
              onClick={() => demo.mutate()}
              disabled={demo.isPending}
            >
              <Icon name="play" /> {demo.isPending ? "Wird angelegt …" : "Demo-Auftrag starten"}
            </button>
          )}
        </div>
      </section>

      <RolloutPanel isOwner={auth.user?.role === "owner"} />
      {/* Ein Stapel für alles, was dich jetzt braucht; jede Zeile hat genau einen Knopf (UX-Umbau, Phase 2). */}
      <section className="panel panel--section now-stack" aria-labelledby="nowHead">
        <div className="panel-head">
          <h2 id="nowHead">Jetzt dran</h2>
          <span className="muted" role="status">
            {nowCount === 0 ? "Alles ruhig." : `${nowCount} offen`}
          </span>
        </div>
        {urgentTasks > 0 && (
          <div className="review-due">
            <span>
              <strong>
                {urgentTasks === 1 ? "Ein dringender Schritt" : `${urgentTasks} dringende Schritte`} bei dir
              </strong>
              <span className="muted"> · mit Befehlen zum Kopieren</span>
            </span>
            <Link to="/" hash="fuer-dich" className="btn btn--primary btn--small">
              Zu „Für dich zu tun“
            </Link>
          </div>
        )}
        {open[0] && (
          <div className="now-decision">
            <DecisionCard key={open[0].id} decision={open[0]} compact />
            {open.length > 1 && (
              <Link to="/decisions" className="link">
                {open.length - 1} weitere Entscheidung{open.length > 2 ? "en" : ""} <Icon name="arrow" />
              </Link>
            )}
          </div>
        )}
        {waiting > 0 && (
          <div className="review-due">
            <span>
              <strong id="approvalsDue">
                {waiting} {waiting === 1 ? "Auftrag wartet" : "Aufträge warten"} auf deine Freigabe
              </strong>
              <span className="muted"> · alles an einer Stelle freigeben</span>
            </span>
            <Link to="/decisions" className="btn btn--primary btn--small">
              Zur Sammelfreigabe
            </Link>
          </div>
        )}
        {reviewDue && (
          <div className="review-due">
            <span>
              <strong id="reviewDue">Wochenrückblick fällig</strong>
              <span className="muted"> · zehn Minuten für Fertiges, Fokus und den nächsten Schritt</span>
            </span>
            <Link to="/review" className="btn btn--primary btn--small">
              Rückblick starten
            </Link>
          </div>
        )}
        {limitNear && (
          <div className="review-due">
            <span>
              <strong>Ein Abo-Limit ist nahe oder erreicht</strong>
              <span className="muted"> · Stand wie von Claude gemeldet</span>
            </span>
            <span className="now-actions">
              <Link to="/usage" className="btn btn--primary btn--small">
                Zum Verbrauch
              </Link>
              <button type="button" className="chip-btn" onClick={dismissLimit}>
                <Icon name="check" /> Gesehen
              </button>
            </span>
          </div>
        )}
        {nowCount === 0 && (
          <p className="muted">Keine Entscheidung, keine Freigabe, kein Rückblick fällig.</p>
        )}
      </section>
      <OwnerTasksPanel />

      <div className="panel panel--section cockpit-orders" aria-labelledby="ordersTitle">
        <div className="panel-head">
          <h2 id="ordersTitle">Aufträge</h2>
          <Link to="/projects" className="link">
            Alle Projekte <Icon name="arrow" />
          </Link>
        </div>
        {orderProjects.length > 1 && (
          <div className="list-filter">
            <label htmlFor={filterId}>Projekt</label>
            <select id={filterId} value={projectFilter} onChange={(e) => setProjectFilter(e.target.value)}>
              <option value="">Alle Projekte</option>
              {orderProjects.map(([id, title]) => (
                <option key={id} value={id}>
                  {title}
                </option>
              ))}
            </select>
          </div>
        )}
        {workOrders.isLoading && <p className="muted">Lade …</p>}
        {!workOrders.isLoading && list.length === 0 && (
          <EmptyState
            icon="network"
            title="Noch keine Aufträge"
            text="Lege ein Projekt an und starte einen Auftrag, um die Flow Map live zu sehen."
          />
        )}
        <ul className="order-list">{[...active, ...other].map(orderCard)}</ul>
        {archived.length > 0 && (
          <details className="fold order-archive">
            <summary>
              <strong>Archiv ({archived.length})</strong>{" "}
              <span className="muted">ausgeliefert oder abgebrochen</span>
            </summary>
            <ul className="order-list">{archived.map(orderCard)}</ul>
          </details>
        )}
      </div>

      <details className="panel fold trial-fold">
        <summary>
          <h2>Bewährung bis 13.11.</h2> <span className="muted">Kennzahlen für die Auswertung</span>
        </summary>
        <TrialPanel />
        <EfficiencyPanel />
      </details>

      <section className="bottom">
        <Activity events={events.data ?? []} title="Aktivität im Workspace" limit={6} />
      </section>
      <p className="muted small">
        <Link to="/usage" className="link">
          Verbrauch und Kosten <Icon name="arrow" />
        </Link>
      </p>
    </>
  );
}
